'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import Swimlane from '@/app/[locale]/(worlds)/components/Swimlane';
import { useExploreAllSectionsData } from '@/app/[locale]/(worlds)/components/useExploreAllSectionsData';
import IconExperimence from '@/components/assets/icon_experimence.svg';
import IconGamepad from '@/components/assets/icon_gamepad.svg';
import IconVideo from '@/components/assets/icon_video.svg';
import { LoadingDots } from '@/components/LoadingDots';
import ViverseWorldCard from '@/components/ViverseWorldCard';
import { ViverseWorldCardSkeleton } from '@/components/ViverseWorldCardSkeleton';
import { SWIMLANE_SCROLL_BLEED_CLASS } from '@/constants/swimlane';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useLoadMore } from '@/hooks/useLoadMore';
import { useSearchParamsSchema } from '@/hooks/useSearchParamsSchema';
import type { WorldCardInfo } from '@/types/viverse-world-card';
type ExploreViverseAllSectionsProps = {
  activeTag: string;
};
const GAMES_GRID = 'grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6 1.5xl:grid-cols-4';
function GamesGrid(t0) {
  const $ = _c(14);
  const {
    rooms,
    title,
    dataRoomIdPrefix,
    hideLiked
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <div className="subheader-3 lg:subheader-2 mb-2 font-semibold md:py-2 lg:font-semibold">{title}</div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== dataRoomIdPrefix || $[3] !== hideLiked || $[4] !== rooms) {
    let t3;
    if ($[6] !== dataRoomIdPrefix || $[7] !== hideLiked) {
      t3 = world => <ViverseWorldCard key={world.id} info={world} data-room-id={world.id ? `${dataRoomIdPrefix}:${world.id}` : undefined} className="h-auto p-0" imageClassName="aspect-video w-full object-cover" hideLiked={hideLiked} hidePremiumBadge={false} cardType="games" />;
      $[6] = dataRoomIdPrefix;
      $[7] = hideLiked;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = rooms.map(t3);
    $[2] = dataRoomIdPrefix;
    $[3] = hideLiked;
    $[4] = rooms;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = <div className={GAMES_GRID}>{t2}</div>;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t1 || $[12] !== t3) {
    t4 = <div className="px-4 lg:px-6">{t1}{t3}</div>;
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
type SectionPageData = {
  games: WorldCardInfo[];
  videos: WorldCardInfo[];
  experiences: WorldCardInfo[];
};
export function SectionPageBlock(t0) {
  const $ = _c(19);
  const {
    page,
    dataRoomIdPrefix: t1,
    hideLiked: t2
  } = t0;
  const dataRoomIdPrefix = t1 === undefined ? "ev" : t1;
  const hideLiked = t2 === undefined ? true : t2;
  const t = useTranslations();
  let t3;
  if ($[0] !== dataRoomIdPrefix || $[1] !== hideLiked || $[2] !== page.games || $[3] !== t) {
    t3 = page.games.length > 0 && <GamesGrid rooms={page.games} dataRoomIdPrefix={dataRoomIdPrefix} hideLiked={hideLiked} title={<span className="flex items-center gap-2"><IconGamepad className="size-6 shrink-0 fill-white" />{t("worlds-landing.explore-viverse.content-type.game")}</span>} />;
    $[0] = dataRoomIdPrefix;
    $[1] = hideLiked;
    $[2] = page.games;
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== dataRoomIdPrefix || $[6] !== hideLiked || $[7] !== page.videos || $[8] !== t) {
    t4 = page.videos.length > 0 && <Swimlane title={<span className="flex items-center gap-2"><IconVideo className="size-6 shrink-0 fill-white" />{t("worlds-landing.explore-viverse.content-type.video")}</span>} data-component="explore-videos-swimlane" cardVariant="viverse" rooms={page.videos} dataRoomIdPrefix={dataRoomIdPrefix} hideLiked={hideLiked} hidePremiumBadge={false} scrollClassName={`${SWIMLANE_SCROLL_BLEED_CLASS} gap-4`} />;
    $[5] = dataRoomIdPrefix;
    $[6] = hideLiked;
    $[7] = page.videos;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== dataRoomIdPrefix || $[11] !== hideLiked || $[12] !== page.experiences || $[13] !== t) {
    t5 = page.experiences.length > 0 && <Swimlane title={<span className="flex items-center gap-2"><IconExperimence className="size-6 shrink-0 fill-white" />{t("worlds-landing.explore-viverse.content-type.experience")}</span>} data-component="explore-experiences-swimlane" cardVariant="viverse" rooms={page.experiences} dataRoomIdPrefix={dataRoomIdPrefix} hideLiked={hideLiked} hidePremiumBadge={false} scrollClassName={`${SWIMLANE_SCROLL_BLEED_CLASS} gap-4`} />;
    $[10] = dataRoomIdPrefix;
    $[11] = hideLiked;
    $[12] = page.experiences;
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = <div className="flex flex-col gap-6">{t3}{t4}{t5}</div>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
export function SectionPageSkeleton(t0) {
  const $ = _c(2);
  const {
    gamesCount
  } = t0;
  let t1;
  if ($[0] !== gamesCount) {
    t1 = <div className="flex flex-col gap-6"><div className="px-4 lg:px-6"><div className={GAMES_GRID}>{Array.from({
            length: gamesCount
          }).map(_temp)}</div></div></div>;
    $[0] = gamesCount;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp(_, i) {
  return <ViverseWorldCardSkeleton key={`games-sk-${i}`} className="h-auto p-0" />;
}
export default function ExploreViverseAllSections(t0) {
  const $ = _c(27);
  const {
    activeTag
  } = t0;
  const {
    store
  } = useConfigsContext();
  const {
    device,
    sort
  } = useSearchParamsSchema();
  const htcToken = store?.credentials?.htcToken;
  let t1;
  if ($[0] !== activeTag || $[1] !== device || $[2] !== htcToken || $[3] !== sort) {
    t1 = {
      activeTag,
      htcToken,
      sort,
      device
    };
    $[0] = activeTag;
    $[1] = device;
    $[2] = htcToken;
    $[3] = sort;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    pages,
    gamesPageLimit,
    enableLoadMore,
    isLoading,
    pagination
  } = useExploreAllSectionsData(t1);
  let t2;
  if ($[5] !== enableLoadMore || $[6] !== pagination) {
    t2 = {
      enabled: enableLoadMore,
      ...pagination
    };
    $[5] = enableLoadMore;
    $[6] = pagination;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const loadMoreRef = useLoadMore(t2);
  let t3;
  if ($[8] !== pages) {
    t3 = pages.map(_temp2);
    $[8] = pages;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== gamesPageLimit || $[11] !== isLoading || $[12] !== pages.length) {
    t4 = isLoading && pages.length === 0 && <SectionPageSkeleton gamesCount={gamesPageLimit} />;
    $[10] = gamesPageLimit;
    $[11] = isLoading;
    $[12] = pages.length;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== enableLoadMore || $[15] !== loadMoreRef) {
    t5 = enableLoadMore && <div ref={loadMoreRef} className="h-1" />;
    $[14] = enableLoadMore;
    $[15] = loadMoreRef;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <div className="flex flex-col gap-10">{t3}{t4}{t5}</div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== pages.length || $[22] !== pagination.isValidating) {
    t7 = pagination.isValidating && pages.length > 0 && <LoadingDots />;
    $[21] = pages.length;
    $[22] = pagination.isValidating;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t6 || $[25] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}
function _temp2(page, i) {
  return <SectionPageBlock key={i} page={page} />;
}